export interface AuthCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

export interface SessionData {
  accessToken: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
  expiresAt: number;
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
const TOKEN_KEY = 'admin_auth_token';
const USER_KEY = 'admin_auth_user';
const EXPIRY_KEY = 'admin_auth_expiry';

export async function login(credentials: AuthCredentials): Promise<AuthResponse> {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Login failed');
  }

  const data: AuthResponse = await response.json();
  
  // Store session data
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); // 24 hours
  }

  return data;
}

export function logout(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  }
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser() {
  if (typeof window === 'undefined') return null;
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
}

export function isSessionValid(): boolean {
  if (typeof window === 'undefined') return false;
  
  const token = getToken();
  const expiry = localStorage.getItem(EXPIRY_KEY);

  if (!token || !expiry) return false;

  const expiryTime = parseInt(expiry, 10);
  return Date.now() < expiryTime;
}

export function getSession(): SessionData | null {
  if (typeof window === 'undefined') return null;
  
  if (!isSessionValid()) {
    logout();
    return null;
  }

  const token = getToken();
  const user = getUser();

  if (!token || !user) return null;

  return {
    accessToken: token,
    user,
    expiresAt: parseInt(localStorage.getItem(EXPIRY_KEY) || '0', 10),
  };
}
